/* GridColumnRenderer.js */

"use strict";

const _ = require('lodash');

class GridColumnRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("grid-column");
        this._rootElem.component = this._model;

        this._columnName = document.createElement('div');
        this._columnName.classList.add('column-name');
        this._sortContainer = document.createElement('a');
        this._sortContainer.classList.add('sort-container');
        this._sortIcon = document.createElement('base-icon-gl');
        this._sortIcon.dataset.icon = 'toggle.sort';
        this._sortContainer.appendChild(this._sortIcon);
        this._rootElem.appendChild(this._columnName);
        this._rootElem.appendChild(this._sortContainer);

        this._registerListeners();
        this.render();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown(){
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._model = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            this._columnName = undefined;
            this._sortContainer = undefined;
            this._sortIcon = undefined;
            resolve();
        });
        return prom;
    }
    _registerListeners(){
        this._model.on("ID",this._onId = this._onId.bind(this));
        this._model.on("Width",this._onWidth = this._onWidth.bind(this));
        this._model.on("Label",this._onLabel = this._onLabel.bind(this));
        this._model.on("Sortable",this._onSortable = this._onSortable.bind(this));
        this._model.on("Sort",this._onSort = this._onSort.bind(this));
        this._model.on("ColumnRendererComponent",this._onColumnRendererComponent = this._onColumnRendererComponent.bind(this));
        this._columnName.addEventListener('click', this._onSortContainerClick = this._onSortContainerClick.bind(this));
        this._sortContainer.addEventListener('click', this._onSortContainerClick = this._onSortContainerClick.bind(this));
    }
    _unregisterListeners(){
        this._model.removeListener("ID",this._onId);
        this._model.removeListener("Width",this._onWidth);
        this._model.removeListener("Label",this._onLabel);
        this._model.removeListener("Sortable",this._onSortable);
        this._model.removeListener("Sort",this._onSort);
        this._model.removeListener("ColumnRendererComponent",this._onColumnRendererComponent);
        this._columnName.removeEventListener('click', this._onSortContainerClick);
        this._sortContainer.removeEventListener('click', this._onSortContainerClick);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render(){
        let prom = new Promise((resolve/*,reject*/) => {
            this._setId(this._model.getId());
            this._setWidth(this._model.getWidth());
            this._setLabel(this._model.getLabel());
            this._setSortable(this._model.getSortable());
            this._setSort(this._model.getSort());
            this._setColumnRendererComponent(this._model.getColumnRendererComponent());
            resolve();
        });
        return prom;
    }
    get rootElement(){
        return this.getRootElement();
    }
    getRootElement(){
        return this._rootElem;
    }
    get model(){
        return this.getModel();
    }
    getModel(){
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when the id has been updated in the model
     * @param newVal {string}
     * @private
     */
    _onId(newVal){
        this._setId(newVal);
    }

    /**
     * Fired when the width has been updated in the model
     * @param newVal {number}
     * @private
     */
    _onWidth(newVal){
        this._setWidth(newVal);
    }

    /**
     * Fired when the label has been updated in the model
     * @param newVal {string}
     * @private
     */
    _onLabel(newVal){
        this._setLabel(newVal);
    }

    /**
     * Fired when the sortable state has been updated in the model
     * @param newVal {boolean}
     * @private
     */
    _onSortable(newVal){
        this._setSortable(newVal);
    }

    /**
     * Fired when the sorted state has been updated in the model
     * @param newVal {string}
     * @private
     */
    _onSort(newVal){
        this._setSort(newVal);
    }

    /**
     * Fired when columnRendererComponent has been updated in the model
     * @param newVal {component}
     * @private
     */
    _onColumnRendererComponent(newVal){
        this._setColumnRendererComponent(newVal);
    }

    /**
     * Fired when header container has been clicked
     * @param event {event}
     * @private
     */
    _onSortContainerClick(event){
        let sortable = this._model.getSortable();
        if (sortable === true) {
            let sort = this._model.getSort().toLowerCase();
            if (sort === "asc") {
                this._model.setSort('desc');
            } else {
                this._model.setSort('asc');
            }
            this._model._triggerUserSorted(event);
        }
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Set the ID as a class in the DOM
     * @param newVal {string}
     * @private
     */
    _setId(newVal){
        if (!_.isUndefined(newVal)) {
            this._rootElem.classList.add(newVal);
        }
    }

    /**
     * Set the width (in numeric percentage) in the DOM
     * @param newVal {double}
     * @private
     */
    _setWidth(newVal){
        if (!_.isUndefined(newVal)) {
            this._rootElem.style.width = newVal * 100 + '%';
        }
    }

    /**
     * Set the column display name
     * @param newVal {string}
     * @private
     */
    _setLabel(newVal){
        this._columnName.innerHTML = newVal;
    }

    /**
     * Toggle the sortable state
     * @param newVal {boolean}
     * @private
     */
    _setSortable(newVal){
        if (newVal === true) {
            this._rootElem.classList.remove('unsortable');
        } else {
            this._rootElem.classList.add('unsortable');
        }
    }

    /**
     * Toggle the sorted state
     * @param newVal {string}
     * @private
     */
    _setSort(newVal){
        switch (newVal.toLowerCase()) {
            case "asc":
                this._rootElem.classList.add('sorted');
                this._rootElem.classList.add('ascending');
                break;
            case "desc":
                this._rootElem.classList.add('sorted');
                this._rootElem.classList.remove('ascending');
                break;
            default:
                this._rootElem.classList.remove('sorted');
                this._rootElem.classList.remove('ascending');
                break;
        }
    }

    /**
     * Do something with the columnRendererComponent
     * @param newVal {Boolean}
     * @private
     */
    _setColumnRendererComponent(/*newVal*/){
        // Here if needed
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = GridColumnRenderer;